"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const map_1 = __importDefault(require("lodash/map"));
const keyBy_1 = __importDefault(require("lodash/keyBy"));
const values_1 = __importDefault(require("lodash/values"));
const forEach_1 = __importDefault(require("lodash/forEach"));
const groupBy_1 = __importDefault(require("lodash/groupBy"));
const mapValues_1 = __importDefault(require("lodash/mapValues"));
const isPlainObject_1 = __importDefault(require("lodash/isPlainObject"));
const matches_1 = __importDefault(require("lodash/matches"));
const selectors_1 = require("@zedoc/selectors");
const reselect_1 = require("reselect");
const pickAnyKey_1 = __importDefault(require("../utils/pickAnyKey"));
const sorting_1 = require("../utils/sorting");
const identity = (x) => x;
const constant = (x) => () => x;
const property = (name) => {
    const getAtKey = (0, selectors_1.createGetAtKey)(name);
    return (state, props) => getAtKey(props);
};
const createEntitiesSelectors = (collection, { Model, plugins, prefix = 'ddp', transform: transformRawObject, transformContextSelector, selectEntities = (state) => state &&
    state[prefix] &&
    state[prefix].entities &&
    state[prefix].entities[collection], } = {}) => {
    const empty = {};
    // NOTE: Values mapping selector returns the unchanged value if it's empty. This
    //       means that if we pass null, then it will return the same null, not empty
    //       object as in the case of lodash.mapValues.
    const selectEntitiesOrEmpty = (state) => selectEntities(state) || empty;
    let mapOneObject;
    if (Model && transformRawObject) {
        mapOneObject = (rawObject, ctx) => new Model(transformRawObject(rawObject, ctx));
    }
    else if (Model) {
        mapOneObject = (rawObject) => new Model(rawObject);
    }
    else if (transformRawObject) {
        mapOneObject = transformRawObject;
    }
    const selectAll = mapOneObject
        ? (0, selectors_1.higherOrderSelector)(transformContextSelector || constant(null), (transformContext) => {
            return (0, selectors_1.valuesMappingSelector)(selectEntitiesOrEmpty, (object) => mapOneObject(object, transformContext));
        })
        : selectEntitiesOrEmpty;
    const createListSelector = (selectDocs, selectSorter = constant(sorting_1.nilSorter)) => (0, reselect_1.createSelector)(selectDocs, (0, sorting_1.toSorterSelector)(selectSorter), (docs, compare) => compare === sorting_1.nilSorter ? (0, values_1.default)(docs) : (0, values_1.default)(docs).sort(compare));
    const createItemSelector = (selectDocs, selectSorter) => (0, reselect_1.createSelector)(createListSelector(selectDocs, selectSorter), (list) => list && list[0]);
    const createSubsetSelectorCreator = (selectLimit, selectSorter) => (selectDocs) => (0, selectors_1.reconcilingSelector)((0, selectors_1.toSelector)(selectLimit), (0, reselect_1.createSelector)(selectDocs, (0, sorting_1.toSorterSelector)(selectSorter), (docs, compare) => {
        const list = (0, map_1.default)(docs, (doc, id) => ({ doc, id }));
        if (compare === sorting_1.nilSorter) {
            return list;
        }
        return list.sort((x, y) => compare(x.doc, y.doc));
    }), (limit, sortedDocs) => (0, mapValues_1.default)((0, keyBy_1.default)(sortedDocs.slice(0, limit), 'id'), 'doc'));
    const createSelectOne = (selectDocs, selectSorter) => (selectId) => {
        let idSelector = selectId;
        if (typeof idSelector === 'string') {
            idSelector = property(idSelector);
        }
        if (!idSelector) {
            if (selectSorter) {
                return createItemSelector(selectDocs, selectSorter);
            }
            idSelector = (0, reselect_1.createSelector)(selectDocs, (docs) => (0, pickAnyKey_1.default)(docs));
        }
        return (0, reselect_1.createSelector)(idSelector, selectDocs, (id, docs) => id ? docs && docs[id] : undefined);
    };
    const filter = (selectDocs, selectPredicate, transform = identity) => {
        if (!selectPredicate) {
            return selectDocs;
        }
        let predicateSelector;
        if ((0, isPlainObject_1.default)(selectPredicate)) {
            predicateSelector = (0, selectors_1.toSelector)(selectPredicate);
        }
        else if (typeof selectPredicate === 'function') {
            predicateSelector = selectPredicate;
        }
        else {
            throw new Error(`Expected a plain object or selector, got ${typeof selectPredicate}`);
        }
        const selectPredicateValues = (0, reselect_1.createSelector)((0, reselect_1.createSelector)(predicateSelector, (predicate) => {
            let compiled = predicate;
            if (typeof predicate === 'object') {
                compiled = (0, matches_1.default)(predicate);
            }
            else if (typeof predicate !== 'function') {
                throw new Error('Selector expects predicate to be an object or a function');
            }
            const selector = (0, selectors_1.valuesMappingSelector)(selectDocs, (doc, id) => compiled(doc, id));
            return selector;
        }), identity, (valuesSelector, state) => valuesSelector(state));
        return (0, selectors_1.valuesMappingSelector)((0, reselect_1.createSelector)(selectDocs, selectPredicateValues, (docs, predicateValues) => {
            const results = {};
            (0, forEach_1.default)(predicateValues, (accepted, id) => {
                if (accepted && docs[id]) {
                    results[id] = docs[id];
                }
            });
            return results;
        }), transform);
    };
    const lookup = (selectDocs, { from = selectDocs, key, foreignKey, as } = {}) => {
        let getRelated;
        if (typeof key === 'function') {
            getRelated = key;
        }
        else if (!key || typeof key === 'string') {
            const getKey = key ? (0, selectors_1.createGetAtKey)(key) : (doc, id) => id;
            getRelated = (doc, id, byForeignKey) => byForeignKey[getKey(doc, id)] || [];
        }
        else {
            getRelated = constant([]);
        }
        const selectJoin = (0, selectors_1.reconcilingSelector)(selectDocs, (0, selectors_1.reconcilingSelector)(from, (otherDocs) => (0, groupBy_1.default)(otherDocs, foreignKey)), (docs, byForeignKey) => (0, mapValues_1.default)(docs, (doc, id) => ({
            doc,
            related: getRelated(doc, id, byForeignKey),
        })));
        return (0, selectors_1.valuesMappingSelector)(selectJoin, ({ doc, related }) => Object.create(doc, {
            [as]: {
                enumerable: false,
                value: related,
            },
        }));
    };
    const selectTransformedDocs = (selectDocs, selectTransform) => (0, selectors_1.higherOrderSelector)((0, selectors_1.toSelector)(selectTransform), (transform) => (0, selectors_1.valuesMappingSelector)(selectDocs, transform));
    const assignMethods = (createUtility, createSubsetSelector, selectDocs, selectSorter, slector) => Object.assign(slector, {
        byId: () => createSubsetSelector(selectDocs),
        where: (selectPredicate) => createUtility(filter(selectDocs, selectPredicate), selectSorter),
        whereIdEquals: (selectId) => createUtility(filter(selectDocs, (0, reselect_1.createSelector)((0, selectors_1.toSelector)(selectId), (id) => (doc, docId) => id === docId)), selectSorter),
        whereIdMatchesProp: (prop) => createUtility(filter(selectDocs, (0, reselect_1.createSelector)(property(prop), (id) => (doc, docId) => id === docId)), selectSorter),
        satisfying: (predicate) => createUtility(filter(selectDocs, constant(predicate)), selectSorter),
        sort: (selectAnotherSorter) => createUtility(selectDocs, (0, sorting_1.composeSorterSelector)(selectSorter, selectAnotherSorter)),
        withCompare(compare) {
            if (typeof compare !== 'function') {
                throw new Error('Expected "compare" to be a function');
            }
            return createUtility(selectDocs, (0, sorting_1.composeSorterSelector)(selectSorter, constant(compare)));
        },
        limit: (selectLimit) => createUtility(createSubsetSelectorCreator(selectLimit, selectSorter)(selectDocs), selectSorter),
        lookup: (options) => createUtility(lookup(selectDocs, options), selectSorter),
        map: (selectTransform) => createUtility(selectTransformedDocs(selectDocs, selectTransform), selectSorter),
    }, (0, mapValues_1.default)(plugins, (plugin) => (...args) => plugin(...args)(createUtility(selectDocs, selectSorter))));
    const createAllUtility = (selectDocs, selectSorter) => assignMethods(createAllUtility, identity, selectDocs, selectSorter, createListSelector(selectDocs, selectSorter));
    const createOneUtility = (selectDocs, selectSorter) => assignMethods(createOneUtility, createSubsetSelectorCreator(1, selectSorter), selectDocs, selectSorter, createSelectOne(selectDocs, selectSorter)());
    const createPredicate = (accept = constant(true), ...selectors) => (0, reselect_1.createSelector)(...selectors, (...options) => (doc) => accept(doc, ...options));
    // Example usage:
    //
    // select(Todo).one()
    // select(Todo).one.id()
    // select(Todo).one.where()
    // select(Todo).where()
    // select(Todo).where().byId()
    // select(TodoList).all().lookup({
    //   from: select(Todo).all(),
    //   foreignKey: 'listId',
    //   as: 'todos',
    // })
    const where = (selectPredicate) => createAllUtility(filter(selectAll, selectPredicate));
    const one = assignMethods(createOneUtility, createSubsetSelectorCreator(1), selectAll, null, () => createOneUtility(selectAll));
    const all = assignMethods(createAllUtility, identity, selectAll, null, () => createAllUtility(selectAll));
    return {
        one,
        where,
        all,
        // LEGACY
        find(accept, ...selectors) {
            console.warn('.find() is deprecated, use .all.where() instead');
            return createListSelector(filter(selectAll, createPredicate(accept, ...selectors)));
        },
        findOne(accept, ...selectors) {
            console.warn('.findOne() is deprecated, use .one.where() instead');
            return createItemSelector(filter(selectAll, createPredicate(accept, ...selectors)));
        },
        findAndMap(accept, transform = identity, ...selectors) {
            console.warn('.findAndMap() is deprecated, use .all.where().map() instead');
            return createListSelector(filter(selectAll, createPredicate(accept, ...selectors), transform));
        },
        getOne: createSelectOne(selectAll),
        getAll: createListSelector(selectAll),
        getAllById: selectAll,
    };
};
exports.default = createEntitiesSelectors;
//# sourceMappingURL=createEntitiesSelectors.js.map